import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import {
  CalendarIcon,
  MapPinIcon,
  UsersRoundIcon,
  Building2Icon,
} from 'lucide-react';
import { BackLink, Button, ErrorNotice, LoadingState, Tabs } from '@bcecloud/portal-ui';
import { getToken } from '@/lib/auth';
import type { Event, EventDetailResponse } from '@/types/event';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'documents', label: 'Documents' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'payment', label: 'Invoice & payment' },
  { key: 'faq', label: 'FAQ' },
] as const;

/** Tabs that exist only when the event runs contracts through My Events
 * (Salesforce MyEvents_Contracting__c) — Sertifi/S-Docs events hide them. */
const MYEVENTS_CONTRACTING_TABS = new Set(['contracts', 'payment']);

/** The one tab every event has; the rest BCE can switch off for the portal. */
const ALWAYS_SHOWN_TAB = 'overview';

/** Whether BCE has this tab switched on for the event's portal. Each tab key is its feature key. */
function shownOnPortal(event: Event, tabKey: string): boolean {
  if (tabKey === ALWAYS_SHOWN_TAB) return true;
  return event.visibleFeatures?.[tabKey] !== false;
}

/**
 * Formats a date string for the hero.
 *
 * @param dateStr - ISO date.
 * @returns e.g. "Friday, August 8, 2026".
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Event detail: a branded hero with the essentials, then tabbed content.
 */
export function EventDetailPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState<Event | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchEvent(): Promise<void> {
      setIsLoading(true);
      try {
        const token = getToken();
        const { data } = await axios.get<EventDetailResponse>(`/api/v3/events/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setEvent(data.data);
      } catch {
        if (!cancelled) setEvent(undefined);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    if (id) void fetchEvent();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (id && location.pathname === `/events/${id}`) {
      navigate(`/events/${id}/overview`, { replace: true });
    }
  }, [id, navigate, location.pathname]);

  if (isLoading) return <LoadingState label="Loading event…" />;

  if (!event) {
    return (
      <ErrorNotice
        title="Event not found"
        message="This event may not exist, or you don't have access to it."
        action={
          <Button variant="primary" onClick={() => navigate('/events')}>
            Back to events
          </Button>
        }
      />
    );
  }

  const basePath = `/events/${id ?? ''}`;
  const activeTab = TABS.find((tab) => location.pathname.endsWith(`/${tab.key}`))?.key ?? 'overview';
  const venue = event.startVenue;
  const locationStr = [venue?.city, venue?.state].filter(Boolean).join(', ');
  const timeStr = [event.timeStartString, event.timeEndString].filter(Boolean).join(' - ');

  return (
    <section className="animate-portal-rise">
      <BackLink to="/events">My events</BackLink>

      {/* Hero: the same brand-gradient card language as the portals. */}
      <div
        className="mt-4 rounded-portal-card p-6 text-white"
        // Palette tokens, not the role tokens: brand and brand-sidebar both
        // resolve to royal, which would leave the gradient nothing to travel
        // between.
        style={{
          background: 'linear-gradient(120deg, var(--color-bce-navy), var(--color-bce-royal))',
        }}
      >
        <div className="flex flex-wrap items-center gap-2">
          {event.contractNumber ? (
            <span className="rounded-portal-pill bg-white/15 px-2.5 py-1 text-portal-micro font-bold text-white/90">
              {event.contractNumber}
            </span>
          ) : null}
          {event.stageName ? (
            <span className="rounded-portal-pill bg-white/15 px-2.5 py-1 text-portal-micro font-bold text-white/90">
              {event.stageName}
            </span>
          ) : null}
        </div>

        <h1 className="mt-3 text-[26px] font-bold tracking-[-0.6px]">
          {event.programDisplayName || event.programName || event.opportunityName}
        </h1>

        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-portal-tiny text-white/80">
          {event.eventDate ? (
            <span className="flex items-center gap-1.5">
              <CalendarIcon aria-hidden className="size-4 text-white/60" />
              {formatDate(event.eventDate)}
              {timeStr ? ` · ${timeStr}${event.timeZone ? ` ${event.timeZone}` : ''}` : ''}
            </span>
          ) : null}
          {locationStr ? (
            <span className="flex items-center gap-1.5">
              <MapPinIcon aria-hidden className="size-4 text-white/60" />
              {venue?.name ? `${venue.name}, ${locationStr}` : locationStr}
            </span>
          ) : null}
          {event.participants ? (
            <span className="flex items-center gap-1.5">
              <UsersRoundIcon aria-hidden className="size-4 text-white/60" />
              {event.participants} participants
            </span>
          ) : null}
          {event.account?.name ? (
            <span className="flex items-center gap-1.5">
              <Building2Icon aria-hidden className="size-4 text-white/60" />
              {event.account.name}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-6">
        <Tabs
          label="Event sections"
          items={TABS.filter(
            (tab) =>
              shownOnPortal(event, tab.key) &&
              (event.myEventsContracting === true || !MYEVENTS_CONTRACTING_TABS.has(tab.key)),
          ).map((tab) => ({ key: tab.key, label: tab.label }))}
          active={activeTab}
          onChange={(key) => navigate(`${basePath}/${key}`)}
        />

        <div className="mt-6">
          <Outlet context={{ event, isLoading }} />
        </div>
      </div>
    </section>
  );
}
